import { Check, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const promises = [
  "Rank first in Google for your target suburbs",
  "Build trust instantly with 5-star reviews",
  "Pre-qualify leads automatically — no more tyre-kickers",
  "Win higher-value jobs consistently every week",
  "Look as professional online as your work is on-site",
  "Never miss an enquiry or follow-up again",
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

export function PromiseSection() {
  return (
    <section className="section-padding bg-background-dark relative overflow-hidden" aria-labelledby="promise-heading">
      {/* Premium grid texture */}
      <div className="absolute inset-0 grid-pattern-dark opacity-60" />
      
      {/* Radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] sm:w-[700px] h-[250px] sm:h-[350px] bg-primary/[0.08] rounded-full blur-[100px] sm:blur-[120px]" />

      <div className="container-wide relative">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-6 sm:mb-8"
        >
          <div className="section-badge-dark mb-4 text-xs">
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            <span>What We Deliver</span>
          </div>

          <h2 id="promise-heading" className="heading-section text-foreground-dark text-balance max-w-[800px] mx-auto">
            Imagine Your Trade Business With a Digital Marketing System{" "}
            <span className="text-primary">Built to Win Jobs Every Week</span>
          </h2>
        </motion.div>

        {/* Promise Cards Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 max-w-[1000px] mx-auto mb-6 sm:mb-8"
        >
          {promises.map((promise, index) => (
            <motion.article
              key={index}
              variants={cardVariants}
              className="group flex items-start gap-3 p-4 rounded-xl bg-foreground-dark/[0.05] border border-foreground-dark/10 hover:border-primary/40 transition-all duration-300"
            >
              <div className="w-6 h-6 rounded-full bg-primary/25 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <Check className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
              </div>
              <span className="text-foreground-dark/90 leading-[1.65] text-[0.9375rem] sm:text-base font-medium">{promise}</span>
            </motion.article>
          ))}
        </motion.div>

        {/* Distinction with link to results */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="border-t border-foreground-dark/10 pt-5 sm:pt-6 text-center"
        >
          <p className="text-[0.9375rem] sm:text-base text-foreground-dark/70 mb-1">
            This isn't generic lead gen or random marketing tactics.
          </p>
          <p className="text-base sm:text-lg lg:text-xl font-semibold text-foreground-dark text-balance">
            This is a <span className="text-primary">complete digital growth system</span> built specifically for Australian trade and construction businesses. <a href="#results-section" className="text-primary/80 hover:text-primary hover:underline text-base">See the results →</a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
